import { yupResolver } from "@hookform/resolvers/yup"
import { faQuestionCircle as solidQuestionCircle } from "@jewlr/pro-solid-svg-icons/faQuestionCircle"
import { Box, Flex, FontAwesomeIcon, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Suspense } from "react"
import { useForm, Controller } from "react-hook-form"
import ReactTooltip from "react-tooltip"
import styled from "styled-components"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import api from "apis"
import FloatInput from "components/ui/float-input"

import { InputLabel } from "../payments/payments/components"

import { CancelButton, InputWrapper, SubmitButton } from "./components"

const Tooltip = styled(ReactTooltip)`
  color: ${(props) => props.theme.colors.white};
  margin-left: 10px;
  max-width: 300px;
  text-transform: none;
`

const schema = Yup.object().shape({
  email: Yup.string()
    .required("Please enter a valid email address.")
    .test("test-email", "Please enter a valid email address.", (value) =>
      isEmail(value || "")
    ),
  order_number: Yup.string()
    .typeError("Please enter order/certificate number")
    .max(19, "Maximum length of 19")
    .matches(
      /^((J|j|L|l|B|b)[\d]{10}(-\d{1,7})?|(GE|ge)[\d]{9}[A-Za-z]{2})$/,
      "Invalid format."
    )
    .required("Please enter order/certificate number"),
})

const OrderNumber = ({ rmaType, setData, setOrderNumber, validatePath }) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      email: "",
      order_number: "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const onSubmit = async (values) => {
    await api
      .post(validatePath, {
        email: values.email,
        order_number: values.order_number,
        rma_type: rmaType,
      })
      .then((response) => {
        setData(response.data)
        if (setOrderNumber) {
          setOrderNumber(values.order_number)
        }
      })
      .catch((err) => {
        if (err && err.stack && err.message) {
          // Network OR Server Error
          setError("root.error", {
            message: "Sorry! An error occurred, please try again.",
          })
        } else {
          setError("root.error", {
            message: err?.errors?.[0] || err?.message || err,
          })
        }
      })
  }

  return (
    <Flex flexDirection="column" mt={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {errors?.root?.error && (
          <Typography color="error" mb={1}>
            {(() => {
              const message = errors.root.error.message
              const urlRegex = /(https?:\/\/[^\s]+)/g
              const parts = message.split(urlRegex)
              return parts.map((part, index) => {
                if (part.match(urlRegex)) {
                  return (
                    <a
                      href={part}
                      key={index}
                      rel="noopener noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                      target="_blank"
                    >
                      {part}
                    </a>
                  )
                }
                return <span key={index}>{part}</span>
              })
            })()}
          </Typography>
        )}
        <Box width={{ _: "100%", tablet: "320px" }}>
          <Controller
            control={control}
            name="order_number"
            render={({ field, fieldState: { error } }) => (
              <Box mb={2}>
                <Flex alignItems="baseline">
                  <InputLabel mr="2px">Order/Certificate Number:</InputLabel>
                  <FontAwesomeIcon
                    data-for="ordernum"
                    data-tip
                    fontSize="13px"
                    icon={solidQuestionCircle}
                  />
                </Flex>
                {typeof window !== "undefined" && (
                  <Suspense fallback={<div />}>
                    <Tooltip id="ordernum">
                      The order/certificate number starts with the letter{" "}
                      {sl.config.site_title[0]} and is between 11 to 19
                      alpha-numeric characters
                    </Tooltip>
                  </Suspense>
                )}
                <FloatInput
                  data-cy="order-actions-order-number-input"
                  data-event="click"
                  error={error}
                  id="order_number"
                  placeholder="Order/Certificate Number"
                  variant="checkout"
                  {...field}
                />
              </Box>
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                autoComplete="email"
                containerClassName="mb-16"
                data-cy="order-actions-email-input"
                data-event="click"
                data-for="email"
                data-tip
                error={error}
                id="email"
                label="Email Address"
                placeholder="Email Address"
                variant="checkout"
                {...field}
              />
            )}
          />
        </Box>
        <InputWrapper>
          <SubmitButton
            data-cy="order-actions-order-number-continue-btn"
            loading={isSubmitting}
          >
            Continue
          </SubmitButton>
        </InputWrapper>
        <InputWrapper>
          <CancelButton data-lc="order-number" to="/">
            Cancel and Continue Shopping
          </CancelButton>
        </InputWrapper>
      </form>
    </Flex>
  )
}

OrderNumber.propTypes = {
  rmaType: PropTypes.string,
  setData: PropTypes.func.isRequired,
  setOrderNumber: PropTypes.func,
  validatePath: PropTypes.string.isRequired,
}

export default OrderNumber
